import "client-only";

import posthog from "posthog-js";

import { POSTHOG_DEV_FORCED } from "@/lib/posthog/dev-force";

/**
 * True when a `posthog.capture(...)` from this browser would actually be
 * DELIVERED.
 *
 * The obvious gate — posthog-js's `__loaded` — answers a WEAKER question, and
 * the gap between the two is a real user-visible bug. posthog-js sets
 * `__loaded` at the TOP of `_init`, before it configures opt-out, so any
 * opt-out applied during init reports `__loaded === true` and then silently
 * drops every capture.
 *
 * That distinction is invisible for a fire-and-forget capture — the event was
 * going to be dropped either way. It is NOT invisible for a control the
 * student can see and press: gating a feedback widget on `__loaded` once
 * showed opted-out users (DNT, before DNT skipped init entirely) a fully
 * working affordance. They rate a reply, the thumb fills, the
 * note box opens, the vote persists to localStorage so it is still filled
 * after a reload — and PostHog receives nothing. The student believes they
 * gave feedback.
 *
 * This is deliberately the ONLY exported gate. A second, looser one existed
 * for the fire-and-forget callers, and two predicates one word apart is the
 * footgun that produced the bug above — the next person to add a visible
 * control picks whichever name reads better. The strict answer is correct for
 * both audiences: a caller that would have captured into the void now skips
 * the work instead, which is the same observable behavior.
 *
 * Note this is about INIT and OPT-OUT, not ad-blocking: the capture/ingest
 * endpoint isn't filename-blocked (unlike surveys.js — see CLAUDE.md #37), so
 * ad-blocker users with posthog initialized still submit.
 */
export function isPostHogCapturing(): boolean {
  if (!posthog.__loaded) return false;
  // The dev escape hatch is the one deliberate exception. It inits with
  // `opt_out_capturing_by_default: true` precisely so widgets RENDER locally
  // without writing to the prod project, so it must pass here or the hatch
  // stops doing the job it exists for.
  if (POSTHOG_DEV_FORCED) return true;
  // The SDK's OWN drop gate: `capture()` consults exactly this before sending,
  // so deferring to it means a widget can never disagree with whether the vote
  // actually lands. A hand-rolled `!has_opted_out_capturing()` matches today
  // but misses `cookieless_mode`, under which opted-out users ARE still
  // captured — that copy would hide the control from users whose votes land,
  // the inverse of the bug this function exists for.
  return posthog.is_capturing();
}
